import * as React from "react";
import Modal from "../../ui/modal/Modal";
import CurrencyInputWithIcon from "../../../hiberUiKit/src/atoms/Input/CurrencyInputWithIcon";
import {none, option, Option} from "ts-option";
import {CommonDetailsSummary, DetailsSummaryItem} from "../common/CommonDetailsSummary";
import CurrencyHelper from "../../../helper/CurrencyHelper";
import TextArea from "../../../hiberUiKit/src/atoms/TextArea/TextArea";
import Checkbox from "../../../hiberUiKit/src/atoms/Checkbox/Checkbox";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import ButtonSecondary from "../../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import InvoicePartsAndLabourDetails from "../../../model/invoice/InvoicePartsAndLabourDetails";
import Loading from "../../ui/loading/Loading";
import NumberUtils from "../../../utils/NumberUtils";
import {noop} from "react-select/lib/utils";
import classNames = require("classnames");

interface IComponentProps {
	/**
	 * On successful submit.
	 */
	onSubmit: (invoicePartsAndLabourDetails: InvoicePartsAndLabourDetails, noteContent: string) => void;
	/**
	 * On cancel button click.
	 */
	onCancel: () => void;
	/**
	 * Invoice id.
	 */
	invoiceId: string;
	/**
	 * Indicates if invoice is vat registered.
	 */
	vatRegistered: boolean;
	/**
	 * Vat value
	 */
	vat: Option<number>;
	/**
	 * Labour and parts details.
	 */
	labourAndPartsDetails: Option<InvoicePartsAndLabourDetails>;
	/**
	 * Note - defined if entered before.
	 */
	note: Option<string>;
	/**
	 * If false, changes can be made but total (sum) has to stay the same.
	 */
	allowTotalAmountChange: boolean
}

interface IState {
	/**
	 * If true, data is loading.
	 */
	isDataLoading: boolean;

	/**
	 * Labour.
	 */
	labour: Option<string>,
	/**
	 * Note content.
	 */
	noteContent: Option<string>,
	/**
	 * Confirmation checkbox check indicator.
	 */
	checkboxChecked: boolean;
}

export default class InvoicePartsAndLabourDetailsModal extends React.Component<IComponentProps, IState> {
	/**
	 * Customer subtotal (= total without VAT) before any changes.
	 */
	initialSubtotal: number = 0;
	/**
	 * Customer total before any changes.
	 */
	initialTotal: number = 0;

	constructor(props: IComponentProps) {
		super(props);
		this.state = {
			isDataLoading: true,
			labour: none,
			noteContent: none,
			checkboxChecked: false
		};
	}

	componentDidMount() {
		this.props.labourAndPartsDetails.map(details => {
			this.setState({
				isDataLoading: false,
				labour: option(details.labour.toFixed(2)),
				noteContent: this.props.note
			});
			this.initialSubtotal = details.getSubtotal();
			this.initialTotal = NumberUtils.roundToMaxTwoDecimals(
				details.getVatAmount(this.props.vatRegistered, this.props.vat) + this.initialSubtotal
			);
		});
	}

	render() {
		const vatInclusion = this.props.vatRegistered ? "excl" : "incl";
		const hasSubotalChanged = this.hasSubtotalChanged();
		const hasTotalChanged = this.hasTotalChanged();
		return (
			<Modal customClass="c-invoice-details_adjust-price-modal"
				   onCloseClicked={this.props.onCancel}
				   title={"Adjust labour & parts amount"} autoWidth>
				<Loading isLoading={this.state.isDataLoading}>
					<div className="c-invoice-details_adjust-price-modal_content-container">
						<div className="c-invoice-details_adjust-price-modal_payments-container">
							<div className="c-invoice-details_adjust-price-modal_payment-details-container has-margin-right">
							<span className="c-invoice-details_adjust-price-modal_payment-details-container_container-label">
								{`Payment to hiber (${vatInclusion}. VAT)`}
							</span>
								{this.renderInputWithLabel("hiber Parts",
									this.props.labourAndPartsDetails.map(d => d.hiberFee.toFixed(2)),
									noop, 22, true)}
								{this.renderInputWithLabel("Service charge",
									this.props.labourAndPartsDetails.map(d => d.hiberPartsMargin.toFixed(2)),
									noop, 18, true)}
								{this.isPaymentToHiberEqualToZero() && this.renderHiberZeroPaymentWarning()}
							</div>
							<div className="c-invoice-details_adjust-price-modal_payment-details-container">
							<span className="c-invoice-details_adjust-price-modal_payment-details-container_container-label">
								{`Payment to partner (${vatInclusion}. VAT)`}
							</span>
								{this.renderInputWithLabel("Labour", this.state.labour,
									this.onLabourChange, 22)}
								{this.renderInputWithLabel("Service charge",
									this.props.labourAndPartsDetails.map(d => d.partnerPartsMargin.toFixed(2)),
									noop, 18, true)}
								{this.renderInputWithLabel("Self-sourced parts",
									this.props.labourAndPartsDetails.map(d => d.selfSourcedItemsSubtotal.toFixed(2)),
									noop, 18, true)}
							</div>
						</div>
						{hasSubotalChanged && this.renderSubtotalChangeWarning()}
						{(!hasSubotalChanged && hasTotalChanged) && this.renderTotalChangeWarning()}
						{this.renderSummary(hasSubotalChanged || hasTotalChanged)}
						{this.renderNoteInputWithLabel()}
						<Checkbox requiredMark checked={this.state.checkboxChecked}
								  onChange={this.onCheckboxChange}
								  label={"I confirm the final amount adjusted is correct"}/>
					</div>
					<div className="c-invoice-details_adjust-price-modal_horizontal-line"/>
					{this.renderButtons(hasSubotalChanged || hasTotalChanged)}
				</Loading>
			</Modal>
		);
	}

	renderInputWithLabel(label: string,
		value: Option<string>,
		onChange: (value: Option<string>) => void,
		marginTop: number,
		disabled?: boolean) {
		return <div className="c-invoice-details_adjust-price-modal_payment-details-container_input-with-label"
					style={{marginTop: `${marginTop}px`}}>
			<span className="c-invoice-details_adjust-price-modal_payment-details-container_label">{label}</span>
			<div className="c-invoice-details_adjust-price-modal_payment-details-container_input">
				<CurrencyInputWithIcon value={value} onChange={onChange} disabled={disabled}/>
			</div>
		</div>
	}

	renderHiberZeroPaymentWarning = () => {
		return (
			<div className="c-invoice-details_adjust-price-modal_payment-details-container_warning">
				Sum of these fields cannot be equal to 0.
			</div>
		);
	};

	renderSubtotalChangeWarning = () => {
		const difference = NumberUtils.roundToMaxTwoDecimals(
			this.getStateValuesAsObject().map(d => d.getSubtotal() - this.initialSubtotal).getOrElse(0));
		const warningType = difference > 0 ? "decrease" : "increase";
		return (
			<div className="c-invoice-details_adjust-price-modal_payment-details-container_warning total-warning">
				{`Sum of all fields must equal ${CurrencyHelper.toCurrency(
					this.initialSubtotal)}. Please ${warningType} by ${CurrencyHelper.toCurrency(Math.abs(difference))}`}
			</div>
		);
	};

	renderTotalChangeWarning = () => {
		return (
			<div className="c-invoice-details_adjust-price-modal_payment-details-container_warning total-warning">
				{`Customer total must equal ${CurrencyHelper.toCurrency(this.initialTotal)}. Please modify values.`}
			</div>
		);
	};

	renderSummary = (noMargin: boolean) => {
		if (this.state.isDataLoading) {
			return;
		}
		const details = this.getStateValuesAsObject();
		const subTotal: number = details.map(d => d.getSubtotal()).getOrElse(0);
		const vat: number = details.map(d => d.getVatAmount(this.props.vatRegistered, this.props.vat)).getOrElse(0);
		const total: number = subTotal + vat;
		const summaryItems: DetailsSummaryItem[] = [];

		if (this.props.vatRegistered) {
			const summaryItem1: DetailsSummaryItem = {
				label: "Sub-total (excl. VAT)",
				value: CurrencyHelper.toCurrency(subTotal)
			};
			const summaryItem2: DetailsSummaryItem = {
				label: "VAT",
				value: CurrencyHelper.toCurrency(vat)
			};
			summaryItems.push(summaryItem1, summaryItem2);
		}

		const totalItem: DetailsSummaryItem = {
			label: "Customer total",
			value: CurrencyHelper.toCurrency(total)
		};

		const classFromProps = classNames({
			"c-invoice-details_adjust-price-modal_summary-container": true,
			"no-margin": noMargin
		});

		return (
			<div className={classFromProps}>
				<CommonDetailsSummary items={summaryItems} totalItem={totalItem}/>
			</div>
		);
	};

	renderNoteInputWithLabel = () => {
		return (
			<div className="c-invoice-details_adjust-price-modal_note-input-container">
				<span className="c-invoice-details_adjust-price-modal_note-input-container_label">Leave a note about this amount adjustment</span>
				<span style={{color: "red"}}> *</span>
				<div className="c-invoice-details_adjust-price-modal_note-input-container_input">
					<TextArea value={this.state.noteContent.getOrElse("")}
							  onChange={this.onNoteInputChange}
							  hasBorder
							  rows={5}
							  placeholder={"Notes"}/>
				</div>
			</div>
		);
	};

	renderButtons = (submitDisabled: boolean) => {
		return (
			<div className="c-invoice-details_adjust-price-modal_buttons-container">
				<ButtonSecondary label={"Cancel"} onClick={this.props.onCancel}/>
				<ButtonPrimary label={"Apply adjustment"}
							   onClick={this.onSubmit}
							   disabled={!this.areRequiredInputsFilled() || this.isPaymentToHiberEqualToZero()
							   || submitDisabled
							   }/>
			</div>
		);
	};

	hasSubtotalChanged = () => {
		if (this.props.allowTotalAmountChange) {
			return false;
		}
		return this.initialSubtotal !==
			NumberUtils.roundToMaxTwoDecimals(this.getStateValuesAsObject().map(d => d.getSubtotal()).getOrElse(0));
	};

	hasTotalChanged = () => {
		if (this.props.allowTotalAmountChange) {
			return false;
		}
		return this.initialTotal !==
			NumberUtils.roundToMaxTwoDecimals(
				this.getStateValuesAsObject().map(d => d.getTotal(this.props.vatRegistered, this.props.vat)).getOrElse(0));
	};

	areRequiredInputsFilled = () =>
		this.state.noteContent.map(nc => !!nc.length).getOrElse(false)
		&& this.state.checkboxChecked
		&& this.state.labour.isDefined


	isPaymentToHiberEqualToZero = () => {
		const details = this.getStateValuesAsObject();
		return details.map(d => d.hiberFee + d.hiberPartsMargin === 0).getOrElse(false);
	};

	onSubmit = () => {
		this.props.labourAndPartsDetails.map(
			details => this.props.onSubmit(details.copy({labour: this.state.labour.map(l => Number(l)).getOrElse(0)}),
				this.state.noteContent.get))

	};

	onLabourChange = (labour: Option<string>) => this.setState({labour});

	onNoteInputChange = (evt: React.ChangeEvent<HTMLTextAreaElement>) => this.setState({noteContent: option(evt.target.value)});

	onCheckboxChange = (checkboxChecked: boolean) => this.setState({checkboxChecked});

	getStateValuesAsObject = (): Option<InvoicePartsAndLabourDetails> => {
		return this.props.labourAndPartsDetails.map(
			details => details.copy({labour: this.state.labour.map(l => Number(l)).getOrElse(0)}));
	}
}
